/**
 * src/services/spatial/deck/deckOverlay.ts
 *
 * Shared deck.gl `MapboxOverlay` controller — the single seam through
 * which GeoTrust mounts application-data deck.gl layers on top of the
 * existing Mapbox GL basemap.
 *
 * Why a shared controller (and not one overlay per feature):
 *   - deck.gl's `MapboxOverlay` is a Mapbox `IControl` that shares the
 *     basemap's WebGL context + camera, so layers render in perfect
 *     register with the `gl-spatial-*` extrusions, geofences and
 *     markers — exactly like the existing 3D-Tiles overlay
 *     (`tiles3dOverlay.ts`), just generalised to arbitrary app layers.
 *   - Multiple unrelated React surfaces (spatial objects today; agents,
 *     rules, sessions, presence, drones in later phases) must be able
 *     to contribute layers WITHOUT each creating its own overlay /
 *     canvas. This controller keeps ONE overlay per native map and
 *     merges named "layer groups" into it.
 *
 * The controller is deliberately framework-free so any surface can
 * drive it. React components use `<SpatialDeckOverlay>` which wraps it.
 *
 * MapLibre note: `MapboxOverlay` is compatible with both mapbox-gl and
 * maplibre-gl (MapLibre is a fork with the same `IControl` contract),
 * so this works regardless of which basemap engine App.tsx ends up
 * using.
 */

import { MapboxOverlay } from '@deck.gl/mapbox';
import type { Layer } from '@deck.gl/core';
import { promoteCountryOverlayLayers } from '../../../utils/countryOverlayStyle';

/** Minimal structural type for the underlying mapbox-gl / maplibre-gl map. */
type AnyMap = {
  addControl: (ctrl: unknown) => void;
  removeControl: (ctrl: unknown) => void;
  getCanvas?: () => HTMLCanvasElement | undefined;
  getLayer?: (id: string) => unknown;
  moveLayer?: (id: string, beforeId?: string) => void;
};

export interface DeckOverlayOptions {
  /**
   * Interleave deck layers with the basemap's own 3D content so labels
   * can paint above application polygons. Requires WebGL2; falls back
   * to overlaid mode automatically when WebGL2 is unavailable.
   *
   * Defaults to `false` (overlaid) — the robust, low-risk mode the
   * existing 3D-Tiles overlay uses. Opt into `true` per deployment.
   */
  interleaved?: boolean;
}

/**
 * Detect WebGL2 support with a throwaway canvas so we never downgrade
 * the basemap's live context. Interleaved deck rendering needs WebGL2.
 */
let cachedWebgl2: boolean | null = null;
export function supportsInterleaved(): boolean {
  if (cachedWebgl2 != null) return cachedWebgl2;
  try {
    if (typeof document === 'undefined') {
      cachedWebgl2 = false;
      return cachedWebgl2;
    }
    const c = document.createElement('canvas');
    cachedWebgl2 = !!c.getContext('webgl2');
  } catch {
    cachedWebgl2 = false;
  }
  return cachedWebgl2;
}

export class DeckOverlayController {
  private map: AnyMap;
  private overlay: MapboxOverlay | null = null;
  private groups = new Map<string, Layer[]>();
  private readonly interleaved: boolean;
  private destroyed = false;

  constructor(map: unknown, opts: DeckOverlayOptions = {}) {
    this.map = map as AnyMap;
    // Only interleave when explicitly requested AND WebGL2 is present.
    this.interleaved = !!opts.interleaved && supportsInterleaved();
  }

  private ensureOverlay(): void {
    if (this.overlay || this.destroyed) return;
    this.overlay = new MapboxOverlay({ interleaved: this.interleaved, layers: [] });
    this.map.addControl(this.overlay);
  }

  /** Flattened, order-stable list of every group's layers. */
  private allLayers(): Layer[] {
    const out: Layer[] = [];
    // Map preserves insertion order — first-registered group paints first
    // (lowest), later groups paint on top. `forEach` avoids relying on
    // Map-iterator downleveling (keeps the es5 build target happy).
    this.groups.forEach(layers => {
      for (const l of layers) out.push(l);
    });
    return out;
  }

  private promoteCountryBorders(): void {
    // Interleaved MapboxOverlay inserts custom layer(s) on top of earlier
    // native layers. Re-adding the outline (via promoteCountryOverlayLayers)
    // keeps Manage-Country borders visible like the dashboard (no deck).
    try {
      promoteCountryOverlayLayers(this.map as Parameters<typeof promoteCountryOverlayLayers>[0]);
    } catch {
      /* style not ready */
    }
  }

  private sync(): void {
    if (this.destroyed) return;
    this.ensureOverlay();
    if (!this.overlay) return;
    try {
      this.overlay.setProps({ layers: this.allLayers() });
    } catch {
      /* deck logs its own render errors; never bubble into the host app */
    }
    // setProps applies custom layers asynchronously — promote after the
    // Mapbox style updates so borders stay above the deck overlay.
    this.promoteCountryBorders();
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => this.promoteCountryBorders());
    }
    setTimeout(() => this.promoteCountryBorders(), 0);
    setTimeout(() => this.promoteCountryBorders(), 48);
  }

  /** Register / replace the layers contributed by a named group. */
  setGroup(groupId: string, layers: Layer[]): void {
    if (this.destroyed) return;
    this.groups.set(groupId, layers ?? []);
    this.sync();
  }

  /** Remove a group's contribution (e.g. when a surface unmounts). */
  clearGroup(groupId: string): void {
    if (this.destroyed) return;
    if (this.groups.delete(groupId)) this.sync();
  }

  /** True when at least one group still has layers registered. */
  hasGroups(): boolean {
    return this.groups.size > 0;
  }

  get isInterleaved(): boolean {
    return this.interleaved;
  }

  /** Tear the overlay down and detach it from the map. */
  dispose(): void {
    this.destroyed = true;
    this.groups.clear();
    if (this.overlay) {
      try { this.map.removeControl(this.overlay); } catch { /* ignore */ }
      try { (this.overlay as unknown as { finalize?: () => void }).finalize?.(); } catch { /* ignore */ }
      this.overlay = null;
    }
  }
}

/**
 * One controller per native map instance. Keyed in a WeakMap so the
 * controller is GC'd when the map is discarded, and so React StrictMode
 * double-mounts reuse the same overlay instead of stacking canvases.
 */
const controllers = new WeakMap<object, DeckOverlayController>();

export function getOrCreateDeckOverlay(map: unknown, opts: DeckOverlayOptions = {}): DeckOverlayController {
  const key = map as object;
  let ctrl = controllers.get(key);
  if (!ctrl) {
    ctrl = new DeckOverlayController(map, opts);
    controllers.set(key, ctrl);
  }
  return ctrl;
}

/** Dispose + forget the controller for a given map (call on map teardown). */
export function disposeDeckOverlay(map: unknown): void {
  const key = map as object;
  const ctrl = controllers.get(key);
  if (ctrl) {
    ctrl.dispose();
    controllers.delete(key);
  }
}
